/**
 * What: Shopify results merchant-facing copy.
 * Transport / data-flow: Pure string dictionary segment merged by shopify-component-copy.ts for Shopify UI consumers.
 *
 */
export const shopifyComponentResultsCopy = {
    resultsAllResponsesBrowser: {
        allArms001: 'All versions',
        allRatings002: 'All ratings',
        highIntent45003: 'High intent (4-5)',
        middleIntent254004: 'Middle intent (2.5-4)',
        lowIntent25005: 'Low intent (<2.5)',
        allThemes006: 'All themes',
        uncategorized007: 'Uncategorized',
        browseAllResponses008: 'Browse all responses',
        searchResponses009: 'Search responses',
        searchResponses010: 'Search responses…',
        filterByArm011: 'Filter by version',
        filterByTheme012: 'Filter by theme',
        filterByRating013: 'Filter by rating',
        noResponsesMatchedYourCurrent015: 'No responses matched your current search or filters.',
        question016: '• Question:',
        theme017: '• Theme:',
        showing018: 'Showing',
        of019: 'of',
        responses020: 'responses',
    },
    resultsResultsSections: {
        cancelledHeading: 'This test was cancelled',
        failedHeading: 'This test didn’t finish',
        processingStopped: 'Something went wrong on our side, so the report wasn’t finished. Completed shopper responses are saved.',
        processingCancelled: 'This test was stopped before its report was ready. Completed shopper responses are saved.',
        stoppedProgressHeading: 'Saved progress',
        theseAreDirectionalSyntheticResponse001: 'These are simulated shopper responses; they do not predict real customer behaviour.',
        allReactions002: 'All reactions',
        noThemesMatchingIntent003: 'No response themes match the selected intent.',
    },
    resultsSegmentsTab: {
        noSegmentBreakdownsAreAvailable001: 'No segment breakdowns are available yet.',
        noSegmentBreakdownsWereAvailable002: 'No segment breakdowns were available for this run.',
        audiencePatterns003: 'Audience patterns',
        showsWhichClaimsFitEach004: 'Compares the tested versions within each simulated shopper group using likely buyer share.',
        audienceGroups005: 'Audience groups',
        message006: 'Version tested',
        likelyBuyerShare007: 'Likely buyer share',
        likelyBuyerShareHelp008: 'Likely buyer share is the share of simulated shoppers in each group who said they would likely buy. Results are directional. They compare versions inside this test only.',
    },
    resultsStudyRunProgress: {
        currentProgress001: 'Current progress',
        currentPhase002: 'Current phase: {phase}',
        responseProgress003: '{completed} of {expected} shopper responses complete',
        responseTasksActive004: '{count} response tasks in progress',
        responseTasksWaiting005: '{count} response tasks waiting',
        responseCapacityWaiting: '{count} responses waiting for available capacity. They will continue automatically.',
        responseRetrying: '{count} responses will retry after a temporary error.',
        responseFailed: "We couldn't complete {count} responses.",
        responseRecoveryRequired: 'Some responses need support before this test can continue. Contact support to review the unfinished work.',
        nodeUnits: {
            original_conversations: 'Original conversations',
            decisions: 'Follow-up decisions',
            question_writes: 'Follow-up questions',
            follow_up_answers: 'Follow-up answers',
            analysis_batches: 'Conversation analysis batches',
            consolidations: 'Combined findings',
            reviews: 'Findings reviews',
            publications: 'Results publications',
        },
        phases: {
            waiting: 'Waiting to start',
            preparing_shoppers: 'Preparing shoppers',
            asking: 'Collecting responses',
            scoring: 'Reviewing responses',
            following_up: 'Following up with simulated shoppers',
            analysing: 'Analysing conversations',
            consolidating: 'Combining findings',
            reviewing: 'Checking findings and missing evidence',
            writing_report: 'Preparing the report',
        },
        progressStages: {
            'analyse-study-conversations': 'Conversation analysis batches',
            'collect-summary-notes': 'Collected findings',
            'embed-summary-notes': 'Compared findings',
            'group-summary-notes': 'Grouped findings',
            'merge-summary-findings': 'Combined findings',
            'check-summary-findings': 'Findings checks',
            'finalize-summary-findings': 'Final findings',
            'promote-canonical-artifacts': 'Results publications',
        },
        requiredHeadlines: {
            'reconcile-inquiry-decisions': 'Checking the requested follow-ups',
            'settle-respondent-inquiry': 'Following up with simulated shoppers',
            'analyse-study-conversations': 'Analysing the complete conversations',
            'collect-summary-notes': 'Collecting the conversation findings',
            'embed-summary-notes': 'Comparing similar findings',
            'group-summary-notes': 'Grouping similar findings',
            'merge-summary-findings': 'Combining the conversation findings',
            'check-summary-findings': 'Checking findings and missing evidence',
            'finalize-summary-findings': 'Preparing the final findings',
        },
        nodeOutcomes: {
            succeeded: 'successful',
            failed: 'failed',
            uncertain: 'uncertain',
            skipped: 'skipped',
            cancelled: 'cancelled',
            outstanding: 'outstanding',
        },
        nodeActivities: {
            running: 'Work is in progress.',
            waiting_dependency: 'Waiting for earlier work to finish.',
            capacity_wait: 'Waiting for available capacity.',
            recovering: 'Checking saved work before continuing.',
        },
        nodeCount: '{label}: {succeeded} of {expected} successful',
        nodeCountsUnavailable: 'Saved progress counts are not available yet.',
        nodeDiagnostic: 'Saved progress needs review. Contact support with code {code}.',
        nodeLastSaved: 'Last saved: {time}.',
        completedWithFailures: 'Completed with failures',
        completedWithFailuresDetail: 'Results were published with missing or unsuccessful work. Review the saved coverage before using them.',
        headlinesByTheme: {
            straight: {
                sendParentPlanning: 'Getting your test ready',
                loadSubmissionSnapshot: 'Reviewing your test plan',
                createArmRuns: 'Preparing the versions to compare',
                invokeChildArmRuns: 'Generating simulated shopper reactions',
                aggregateChildResults: 'Comparing the responses',
                promoteCanonicalArtifacts: 'Preparing your report',
            },
            fifties: {
                sendParentPlanning: 'Rolling out the tasting table…',
                loadSubmissionSnapshot: 'Reading your product story over coffee…',
                createArmRuns: 'Lining up your versions on the counter…',
                invokeChildArmRuns: 'Passing the clipboard to simulated shoppers…',
                aggregateChildResults: 'Tallying the scorecards…',
                promoteCanonicalArtifacts: 'Putting the results under the matinee lights…',
            },
            silly: {
                sendParentPlanning: 'Warming up the picture tubes…',
                loadSubmissionSnapshot: 'Bribing the robots with coffee…',
                createArmRuns: 'Polishing the price tags…',
                invokeChildArmRuns: 'Asking the robots to sharpen their pencils…',
                aggregateChildResults: 'Counting raised hands…',
                promoteCanonicalArtifacts: 'Rolling the credits…',
            },
        },
        defaultHeadline006: 'Moving your test forward',
    },
    resultsShopifyStudyResultsPage: {
        back001: 'Back',
        refresh002: 'Refresh',
        resultsCouldNotBeLoaded003: "Couldn't load the results",
        loadingStudyResults004: 'Loading test results…',
        thisSetupDoesNotHave005: 'This setup does not have a linked TestFeed test yet.',
        previousResults006: 'Previous results',
        selectedResult007: 'Selected result',
        noCompletedResultSelected008: 'No completed result selected',
        runTheTestfeedStudyBefore009: 'Run the TestFeed test before reviewing results in Shopify.',
        versions010: 'Test versions',
        sameSetupStyle011: 'Same setup style; useful directional comparison',
        studySetupChanged012: 'Test setup changed; compare directionally',
        audienceChanged013: 'Audience changed; compare cautiously',
        studySettingsChanged014: 'Test settings changed; not directly comparable',
        versionStudy015: 'Test version',
        noScore016: 'No score',
        viewResult017: 'View result',
        selectedAudience018: 'Selected audience',
        relatedAudience019: 'Related audience',
        sample020: 'Sample',
        selectedResult021: 'selected result',
        unavailable022: 'n/a',
        broadShoppers023: 'Broad shoppers',
        shoppers024: 'shoppers',
        up025: 'Up',
        down026: 'Down',
        flat027: 'Flat',
        to028: 'to',
        studyResultsFallbackTitle029: 'Test results',
        viewProduct030: 'View product',
        moreActions031: 'More actions',
        of032: 'of',
        helpModalHeading: 'How test results work',
        helpModalBody1: 'Each version you tested is shown as its own card below, with the score for what this test measures. The outcome above the cards explains whether a version leads and what to do next.',
        helpModalBody2: 'Recommended changes, when there are any, are listed under the cards you can apply directly.',
        resultUnavailableTitle: "This result isn't available",
        resultUnavailableBody: "We couldn't find the finished result for this test. Go back to Tests and open it again, or try again in a few minutes.",
        resultUnavailableRetryAction: 'Try again',
        verdictInfoHeading: 'How this call is made',
        verdictInfoWhy: "This tells you whether to trust the result enough to act on it, or whether you'd need more evidence first.",
        verdictInfoHow: 'We compare the leading scores, their uncertainty ranges and the number of responses. A clear winner meets this test’s decision rules. Close leading scores, too few responses and unfinished results each receive a different outcome and next step.',
    },
    resultsChangeList: {
        heading001: 'Recommended product changes',
        subheading002: 'Discrete recommendations per tested element based on test results.',
        applyButton003: 'Apply change',
        applyPlaceholderToast004: 'Apply feature is coming soon. Changes will be saved in a future update.',
        keepCurrentText005: 'Original product performed best in testing. No changes recommended.',
        inconclusiveText006: 'This element was too close to call in this test.',
        resolutionHeading007: 'What would resolve this',
        advisoryHeading008: 'Manual product update',
        advisoryExplanation009: 'This change cannot be applied automatically. Update manually in Shopify Admin.',
        whereToUpdate010: 'Where to update in Shopify Admin:',
        copyTextButton011: 'Copy text',
        copiedToast012: 'Text copied to clipboard',
        staleWarning013: 'The product was modified in Shopify after this test was created. Re-confirm the original product before applying recommendations.',
        driftDetailsLabel014: 'Product changed:',
        beforeLabel015: 'Original product',
        afterLabel016: 'Proposed change',
        editProposedTextLabel017: 'Proposed text (editable before applying):',
        imageNotice018: 'Adds image to product media gallery and sets as featured image (position 1). Existing media will not be deleted or replaced.',
        priceVariantsNotice019: 'Which variants should this price apply to?',
        allVariantsLabel020: 'All product variants',
        whatChangesLabel030: 'What this changes',
        targetDescriptionAppend031: 'Adds to the product description on',
        targetDescriptionReplace032: 'Replaces the product description on',
        targetVariantPrice034: 'Changes the price on',
        targetProductMedia035: 'Changes the featured image on',
        unnamedProductLabel036: 'your product',
        selectAllVariantsButton037: 'Select all',
        noVariantsSelectedWarning038: 'Choose at least one variant to apply this price.',
        editTextButton039: 'Edit text',
        doneEditingButton040: 'Done',
        showFullDescription041: 'Show full description',
        showLessDescription042: 'Show less',
        grantAccessButton043: 'Grant access',
        grantUnavailableToast044: "The permission request couldn't be opened. Reload the page and try again.",
        grantFailedToast045: "Couldn't request product access. Try again.",
        bestStartingPoint046: 'Best starting point',
        likelyBuyerShare047: 'likely buyer share',
        createNewVersion048: 'Create new version',
        reviewShopperReactions049: 'Review shopper reactions',
        contactTestFeed050: 'Contact TestFeed',
        nextStepHeading051: 'What to do next',
        applySuccessToast021: 'Product change applied successfully.',
        applyErrorToast022: "We couldn't apply that product change.",
        declinedAdvisoryExplanation023: "You chose not to grant product write access, so this change can't be applied automatically. You can make it manually in Shopify Admin, or grant access when you're ready to apply changes from here.",
        staleApplyRefusedToast024: 'Your product changed in Shopify after this test ran. Review the product before applying this recommendation.',
        appliedBadge025: 'Applied to your product',
        revertButton026: 'Undo this change',
        revertSuccessToast027: 'The change was undone in your product.',
        revertErrorToast028: "We couldn't undo this change.",
        revertedBadge029: 'Change undone',
        embeddedImageOption052: 'Image Option',
        staleProductChangedFallbackTitle053: 'Stale — Product Changed',
    },
    resultsTreatmentCard: {
        recommended: 'Recommended',
        checkPrefix: '✓ ',
        bestOptionToTestNext: 'Best option to test next',
        strongestOptionInTest: 'Leaning toward',
        similarPerformance: 'Too close to call',
        notEnoughResponses: 'Not enough responses',
        noProductImage: 'No product image',
        claimPrefix: 'Claim:',
        brandHookPrefix: 'Brand hook:',
        sensoryPrefix: 'Sensory description:',
        usagePrefix: 'Usage description:',
        ingredientPrefix: 'Ingredient detail:',
        messagePrefix: 'Message:',
        promotionPrefix: 'Promotion:',
        testedPrice: 'Tested price',
        newDescription: 'New description',
        newImage: 'New image',
        newClaim: 'New claim',
        newBrandHook: 'New brand hook',
        newSensoryDescription: 'New sensory description',
        newUsage: 'New usage description',
        newIngredient: 'New ingredient detail',
        newMessage: 'New message',
        newPromotion: 'New promotion',
        likelyBuyerShare: 'Likely buyer share',
        likelyBuyerShareHelp: 'Likely buyer share is the share of simulated shoppers who said they would likely buy a version. Results are directional. They compare versions inside this test only.',
        seeReactions: 'See reactions',
        useAsBenchmark: 'Use as benchmark',
        trySomething: 'Try something different',
        productImageAlt: 'Product image',
        imageCaptionPrefix: 'Image caption:',
        priceUnavailable: 'Price unavailable',
        currentProduct: 'Current product',
        original: 'Original',
        testOption: 'Test option',
        optionPrefix: 'Option',
        wordCountSuffix: 'words',
        lengthCaution: 'The winning description is also the longest. Part of the difference may be length, not content.',
        missingDescriptionError: "This description comparison is missing the text for one of its versions, so its results can't be shown. Re-run the test to rebuild the comparison.",
    },
};
//# sourceMappingURL=results.js.map